import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface IgPublishedPost {
  id: string;
  project_id: string;
  queue_id: string | null;
  template_id: string | null;
  category: string;
  format: string;
  caption: string;
  hashtags: string[] | null;
  image_url: string | null;
  image_urls: string[];
  media_type: string;
  ig_media_id: string | null;
  ig_permalink: string | null;
  fb_post_id: string | null;
  published_at: string;
  is_recycled: boolean;
  ig_analytics: {
    engagement_score: number;
    likes: number;
    comments: number;
    impressions: number;
    reach: number;
    saved: number;
    shares: number;
  } | null;
}

export function useIgPublished(limit = 30, projectSlug: string) {
  const [posts, setPosts] = useState<IgPublishedPost[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async (newOffset?: number) => {
    setLoading(true);
    const off = newOffset !== undefined ? newOffset : offset;
    const { data, error } = await supabase.functions.invoke("ig-admin-api", {
      body: { action: "list_published", project: projectSlug, limit, offset: off },
    });
    if (!error && data?.posts) {
      setPosts(data.posts);
      setTotal(data.total || 0);
      if (newOffset !== undefined) setOffset(newOffset);
    }
    setLoading(false);
  }, [limit, offset, projectSlug]);

  useEffect(() => { fetch(0); }, [limit, projectSlug]);

  const nextPage = useCallback(() => { fetch(offset + limit); }, [fetch, offset, limit]);
  const prevPage = useCallback(() => { fetch(Math.max(0, offset - limit)); }, [fetch, offset, limit]);

  return { posts, total, offset, loading, refetch: () => fetch(offset), nextPage, prevPage, hasMore: offset + limit < total };
}
